import React, { useEffect, useMemo, useState } from "react";
import { DatePicker, TimePicker, Select } from "antd";
import Alert from "../components/Alert";
import StepFooter from "./StepFooter";
import moment from "moment";
import { Icon } from "Components";
import { CAMPAIGN_LENGTH_LIST } from "../PhishingManager.constants";
import CampaignLimitToast from "../Phishing/CampaignLimitToast";
import { getPhisingTimeZones, submitStep4 } from "Services/Api/PhishingManager/PhishingManager.Service";
import { errorMessageHandler } from "app-util";

type DeliveryScheduleProps = {
  tenantId: number | string | undefined;
  campaignId: string | number | null;
  onBack: () => void;
  onNext: () => void;
};

type DeleiveryScheduleState = {
  start_date: moment.Moment | null,
  start_date_err: string | null,
  start_time: moment.Moment | null,
  start_time_err: string | null, 
  campaign_length: number, 
  timezone_id: string,
  timezone_id_err: string | null
}

type Timezone = {
  id: number,
  name: string,
  offset: string,
  region: string
};

const toastInitalState = {
  show: false,
  message: ''
}

const Step4DeliverySchedule: React.FC<DeliveryScheduleProps> = ({
  onBack,
  onNext,
  tenantId,
  campaignId
}) => {
  const [state, setState] = useState<DeleiveryScheduleState>({
    start_date: null,
    start_date_err: null,
    start_time: null,
    start_time_err: null,
    campaign_length: 7,
    timezone_id: '',
    timezone_id_err: null
  });
  const [toastState, setToastState] = useState<{ show: boolean, message: string }>({...toastInitalState});
  const [timezoneList, setTimezoneList] = useState<Timezone[]>([])
  const [loading, setLoading] = useState(false);

  const disabledDate = (current: any) => current && (current < moment().startOf("day") || current > moment().add(90, "days"));

  const disableNextBtn: boolean = useMemo(() => {
    const { start_date, start_time, timezone_id, start_date_err, start_time_err, timezone_id_err } = state;
    return !!((!start_date || !start_time || !timezone_id || start_date_err || start_time_err || timezone_id_err) && !loading);
  }, [state])

  const handleSubmit = async () => {
    const { start_date, start_time, timezone_id, campaign_length } = state;

    if(!campaignId || !tenantId) return;
    const errors: Record<string, string> = {};
    if(!start_date) errors['start_date_err'] = "Enter a valid date"
    else if(!start_time) errors['start_time_err'] = "Enter a valid time"
    else if(!timezone_id) errors['timezone_id_err'] = "Select a valid timezone";

    if(Object.keys(errors).length > 0) {
      setState(prev => ({...prev, ...errors}));
      return;
    }

    const payload = {
      start_date: moment(start_date).format("YYYY-MM-DD"),
      start_time: moment(start_time).format("HH:MM"),
      campaign_length,
      timezone_id,
      campaign_id: campaignId
    }
    try {
      setLoading(true);
      const res = await submitStep4(payload, tenantId);
      if(res?.data?.status === "success") onNext();
    } catch (err: any) {
      const errorObj: Record<string, string[]> = err.errors || {};
      console.log(errorObj, "Errror object while submitting step 4 details");
      if(Object.keys(errorObj).length){
        const errors = Object.entries(errorObj).reduce((acc, [key, value]) => {
          if (value?.[0]) acc[key] = value[0];
          return acc;
        }, {} as Record<string, string>);
        setState(prev => ({ ...prev, ...errors }));
      } else {
        const errorMessage = errorMessageHandler(err, "Failed to create campaign step 4");
        setToastState({ show: true, message: errorMessage })
      }
    } finally{
      setLoading(false);
    }
  }

  const dropdownIcon = (
    <div className="cs-dropdownWrapper">
        <Icon type="campaignSetupDropdown" />
    </div>
  );

  useEffect(() => {
    const getTimezones = async () => {
      try {
        if(!tenantId) return;
        const res = await getPhisingTimeZones(tenantId);
        setTimezoneList(res?.data || []);
      } catch (error) {
        setTimezoneList([]);
      }
    }
    getTimezones();
  }, [])

  return (
    <>
      <div className="delivery-schedule-config step-card">
        <CampaignLimitToast
          visible={toastState.show}
          message={toastState.message}
          onClose={() => setToastState({...toastInitalState})}
        />
        <div className="card-header">
          <div className="card-title">Delivery & Timing</div>
          <div className="card-subtitle">
            Set when the emails will be sent and how long the campaign will run
          </div>
        </div>

        <div className="form-container">
          <span className="form-header">Schedule</span>
          <div className="form-section">
            <div className="form-item-row">
              <FormComponent label="Start date" error={state.start_date_err}>
                <DatePicker allowClear={false} value={state.start_date} format="MM/DD/YYYY" className="date-time-picker" disabledDate={disabledDate} onChange={(value) => setState(prev => ({ ...prev, start_date: value, start_date_err: null }))} />
              </FormComponent>

              <FormComponent label="Start time" error={state.start_time_err}>
                <TimePicker className="date-time-picker"
                  allowClear={false}
                  value={state.start_time}
                  onChange={(value) => setState(prev => ({ ...prev, start_time: value, start_time_err: null }))}
                  format="hh:mm A"
                  minuteStep={30}
                />
              </FormComponent>
              
              <FormComponent label="Length of the campaign">
                <Select
                  className="campaign-setup-select"
                  dropdownClassName="campaign-setup-filter-option-select"
                  suffixIcon={dropdownIcon}
                  value={state.campaign_length}
                  onChange={(value) => setState((prev: any) => ({ ...prev, campaign_length: value }))}
                  options={CAMPAIGN_LENGTH_LIST}
                />
              </FormComponent>

              <FormComponent label="Time Zone" error={state.timezone_id_err}>
                <Select
                  placeholder="Select your timezone"
                  disabled={!timezoneList.length}
                  value={state.timezone_id || undefined}
                  onChange={(value) => setState((prev: any) => ({ ...prev, timezone_id: value, timezone_id_err: null }))}
                  suffixIcon={dropdownIcon}
                  className="campaign-setup-select"
                  dropdownClassName="campaign-setup-filter-option-select"
                  optionFilterProp="children">
                  {timezoneList.map((tz) => (
                    <Select.Option key={tz.id} value={tz.id}>
                      {`(${tz.offset}) ${tz.region}`}
                    </Select.Option>
                  ))}
                </Select>
              </FormComponent>
            </div>
          </div>
        </div>

        <div className='alert-section'>
          <Alert title="Delivery notice" subtitle='Emails will be sent incrementally within the selected time window to reduce delivery issues and system load' />
        </div>
      </div>
      <StepFooter
        onBack={onBack}
        onNext={handleSubmit}
        isLoading={loading}
        nextBtnDisabled={disableNextBtn}
      />
    </>
  );
};

export default Step4DeliverySchedule;

const FormComponent = ({ children, label, error = null }: { children: React.ReactNode, label: string, error?: string | null }) => {
  return <div className="form-item">
    <label className="form-label">
      {label} <span className="required-asterisk">*</span>
    </label>
    {children}
    {error && (
      <div className="ml-input-help-text">
        {error}
      </div>
    )}
  </div>
}